import React, { useCallback } from 'react';
import { FN211 } from '../../../services/n211Api';
import { fmt } from '../helpers';

interface Tab2Props {
  details: Partial<FN211>[];
  selectedRow: number;
  isEditing: boolean;
  onSelectRow: (idx: number) => void;
  onSetDetailField: (key: keyof FN211, value: FN211[keyof FN211]) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
}

const Tab2Details: React.FC<Tab2Props> = React.memo(({
  details, selectedRow, isEditing,
  onSelectRow, onSetDetailField, onAddRow, onDeleteRow,
}) => {
  /** Recalculate amount when qty or price changes */
  const handleQtyChange = useCallback((val: string) => {
    const qty = parseFloat(val) || 0;
    onSetDetailField('fn211_14', qty);
    const price = (details[selectedRow]?.fn211_16 as number) || 0;
    onSetDetailField('fn211_19', Math.round(qty * price * 100) / 100);
  }, [onSetDetailField, details, selectedRow]);

  const handlePriceChange = useCallback((val: string) => {
    const price = parseFloat(val) || 0;
    onSetDetailField('fn211_16', price);
    const qty = (details[selectedRow]?.fn211_14 as number) || 0;
    onSetDetailField('fn211_19', Math.round(qty * price * 100) / 100);
  }, [onSetDetailField, details, selectedRow]);

  return (
    <div className="details-tab">
      {/* Detail buttons */}
      <div className="detail-actions">
        <button
          className="btn btn-sm"
          disabled={!isEditing}
          onClick={onAddRow}
        >
          明細新增
        </button>
        <button
          className="btn btn-sm"
          disabled={!isEditing || details.length <= 1}
          onClick={onDeleteRow}
        >
          明細刪除
        </button>
      </div>

      {/* Detail table */}
      <div className="detail-table-wrap">
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>項數</th>
              <th style={{ width: 140 }}>產品代號</th>
              <th style={{ width: 140 }}>批號</th>
              <th style={{ width: 100 }}>交運量</th>
              <th style={{ width: 60 }}>單位</th>
              <th style={{ width: 100 }}>單價</th>
              <th style={{ width: 100 }}>金額</th>
            </tr>
          </thead>
          <tbody>
            {details.map((d, i) => (
              <tr
                key={i}
                className={i === selectedRow ? 'row-selected' : ''}
                onClick={() => onSelectRow(i)}
              >
                <td className="text-center">{d.fn211_02 || i + 1}</td>
                <td>
                  {isEditing && i === selectedRow ? (
                    <input
                      value={String(d.fn211_11 || '')}
                      onChange={e => onSetDetailField('fn211_11', e.target.value)}
                      className="cell-input"
                    />
                  ) : (
                    String(d.fn211_11 || '')
                  )}
                </td>
                <td>
                  {isEditing && i === selectedRow ? (
                    <input
                      value={String(d.fn211_12 || '')}
                      onChange={e => onSetDetailField('fn211_12', e.target.value)}
                      className="cell-input"
                    />
                  ) : (
                    String(d.fn211_12 || '')
                  )}
                </td>
                <td className="text-right">
                  {isEditing && i === selectedRow ? (
                    <input
                      type="number"
                      value={d.fn211_14 ?? ''}
                      onChange={e => handleQtyChange(e.target.value)}
                      className="cell-input text-right"
                      step="any"
                    />
                  ) : (
                    fmt(d.fn211_14 as number)
                  )}
                </td>
                <td>
                  {isEditing && i === selectedRow ? (
                    <input
                      value={String(d.fn211_15 || '')}
                      onChange={e => onSetDetailField('fn211_15', e.target.value)}
                      className="cell-input"
                    />
                  ) : (
                    String(d.fn211_15 || '')
                  )}
                </td>
                <td className="text-right">
                  {isEditing && i === selectedRow ? (
                    <input
                      type="number"
                      value={d.fn211_16 ?? ''}
                      onChange={e => handlePriceChange(e.target.value)}
                      className="cell-input text-right"
                      step="any"
                    />
                  ) : (
                    fmt(d.fn211_16 as number)
                  )}
                </td>
                <td className="text-right">
                  {fmt(d.fn211_19 as number)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Selected row detail panel (Grid-outside fields from Page2) */}
      {details[selectedRow] && (
        <div className="detail-panel">
          <div className="detail-panel-grid">
            <label>色號</label>
            <div>
              {isEditing ? (
                <input
                  value={String(details[selectedRow]?.fn211_13 || '')}
                  onChange={e => onSetDetailField('fn211_13', e.target.value)}
                  className="field-input"
                />
              ) : (
                <span>{String(details[selectedRow]?.fn211_13 || '')}</span>
              )}
            </div>
            <label>色號中文名</label>
            <div>
              {isEditing ? (
                <input
                  value={String(details[selectedRow]?.fn211_17 || '')}
                  onChange={e => onSetDetailField('fn211_17', e.target.value)}
                  className="field-input"
                />
              ) : (
                <span>{String(details[selectedRow]?.fn211_17 || '')}</span>
              )}
            </div>
            <label>色號英文名</label>
            <div>
              {isEditing ? (
                <input
                  value={String(details[selectedRow]?.fn211_18 || '')}
                  onChange={e => onSetDetailField('fn211_18', e.target.value)}
                  className="field-input"
                />
              ) : (
                <span>{String(details[selectedRow]?.fn211_18 || '')}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Tab2Details.displayName = 'Tab2Details';
export default Tab2Details;
